import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Shield } from 'lucide-react';
import { setCookieConsent, trackPageView, hasCookieConsent, hasDeclinedCookies } from '../utils/analytics';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Only show banner if user hasn't made a choice yet
    const hasConsent = hasCookieConsent();
    const hasDeclined = hasDeclinedCookies();
    
    if (!hasConsent && !hasDeclined) {
      // Wait a bit before showing to not interrupt initial page load
      setTimeout(() => setShowBanner(true), 1500);
    }
  }, []);

  const handleAccept = () => {
    setCookieConsent(true);
    setShowBanner(false);
    
    // Dispatch custom event to notify components that consent has changed
    window.dispatchEvent(new Event('cookieConsentChanged'));
    
    // Immediately track a page view with the new session ID
    setTimeout(() => {
      trackPageView();
    }, 100);
  };

  const handleDecline = () => {
    setCookieConsent(false);
    setShowBanner(false);
    
    // Dispatch custom event even on decline
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      style={{ animation: 'slideUp 0.3s ease-out' }}
    >
      <style>
        {`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div className="bg-[#1a1f17] border-t-2 border-[#3d4a35] shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            {/* Icon and Text Content */}
            <div className="flex items-start gap-4 flex-1">
              <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-[#232921] border border-[#3d4a35] flex-shrink-0">
                <Shield className="w-6 h-6 text-[#8b9a7e]" />
              </div>
              <div className="flex-1">
                <h3 
                  id="cookie-consent-title" 
                  className="text-[#e8e4d9] font-semibold text-lg mb-2"
                >
                  Privacy Notice
                </h3>
                <p 
                  id="cookie-consent-description" 
                  className="text-[#a8a293] text-sm leading-relaxed"
                >
                  We use cookies to analyse website traffic and improve your browsing experience. 
                  If you choose to decline, we will not store any personally identifiable information 
                  about your visit. Your choice will be remembered for future visits. 
                  For more details, please read our{' '}
                  <a 
                    href="/privacy-policy" 
                    className="text-[#8b9a7e] hover:text-[#a8b89e] underline transition-colors"
                  >
                    Privacy Policy
                  </a>.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 flex-shrink-0 w-full lg:w-auto">
              <Button
                onClick={handleDecline}
                variant="outline"
                className="flex-1 lg:flex-none px-6 py-2.5 text-sm font-medium text-[#a8a293] bg-transparent border border-[#3d4a35] rounded-md hover:bg-[#232921] hover:text-[#e8e4d9] hover:border-[#4d5a45] transition-all"
                data-testid="cookie-decline-btn"
              >
                Decline
              </Button>
              <Button
                onClick={handleAccept}
                className="flex-1 lg:flex-none px-6 py-2.5 text-sm font-medium text-[#1a1f17] bg-[#8b9a7e] rounded-md hover:bg-[#a8b89e] transition-all"
                data-testid="cookie-accept-btn"
              >
                Accept Cookies
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
